// Animation Context for the unified performance mode.
//
// A single `perfMode` ('auto' | 'pretty' | 'smooth' | 'fastest') is the only
// persisted setting. It DERIVES the two values the rest of the app reads —
// `reduceAnimations` (the `.reduce-animations` kill-switch) and `perfTier`
// (the `.perf-{low|high}` class on <html>) — so every existing consumer keeps
// reading them unchanged.

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { detectDeviceTier } from '../utils/deviceTier';

const AnimationContext = createContext();

const MODE_KEY = 'steadily_perf_mode';
const LEGACY_REDUCE_KEY = 'steadily_reduce_animations';
const LEGACY_TIER_KEY = 'steadily_perf_tier_override';
const VALID_MODES = new Set(['auto', 'pretty', 'smooth', 'fastest']);

// Resolve the initial mode, migrating from the two legacy keys on first load.
function resolveInitialMode() {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    if (VALID_MODES.has(stored)) return stored; // already migrated

    // --- one-time migration from the old two-control model ---
    const reduce = localStorage.getItem(LEGACY_REDUCE_KEY) === 'true';
    const tier = localStorage.getItem(LEGACY_TIER_KEY); // 'low'|'mid'|'high'|null

    let mode;
    if (reduce) mode = 'fastest';              // reduce-animations wins regardless of tier
    else if (tier === 'high') mode = 'pretty';
    else if (tier === 'low') mode = 'smooth';
    else mode = 'auto';                         // 'mid' (Balanced removed) / unset → re-detect

    localStorage.setItem(MODE_KEY, mode);
    return mode;
  } catch {
    // Storage-restricted environments — default to auto.
    return 'auto';
  }
}

// Map a mode to the two values the rest of the app consumes. `auto` re-runs
// device detection (cheap, and stable within a session).
function deriveFromMode(mode) {
  switch (mode) {
    case 'pretty':  return { perfTier: 'high', reduceAnimations: false };
    case 'smooth':  return { perfTier: 'low',  reduceAnimations: false };
    case 'fastest': return { perfTier: 'low',  reduceAnimations: true  };
    case 'auto':
    default:        return { perfTier: detectDeviceTier(), reduceAnimations: false };
  }
}

export const AnimationProvider = ({ children }) => {
  const [perfMode, setPerfModeState] = useState(resolveInitialMode);

  const { perfTier, reduceAnimations } = useMemo(
    () => deriveFromMode(perfMode),
    [perfMode]
  );

  // Apply both classes to <html> in a single effect so the DOM reflects
  // the latest state in one paint.
  useEffect(() => {
    const cls = document.documentElement.classList;
    cls.toggle('reduce-animations', reduceAnimations);
    cls.remove('perf-low', 'perf-mid', 'perf-high');
    cls.add(`perf-${perfTier}`);
  }, [reduceAnimations, perfTier]);

  const setPerfMode = useCallback((mode) => {
    const next = VALID_MODES.has(mode) ? mode : 'auto';
    setPerfModeState(next);
    try { localStorage.setItem(MODE_KEY, next); } catch {}
  }, []);

  const value = useMemo(() => ({
    perfMode,
    setPerfMode,
    // Back-compat: every existing consumer keeps reading these unchanged.
    reduceAnimations,
    perfTier
  }), [perfMode, setPerfMode, reduceAnimations, perfTier]);

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
};

// Custom hook to use animation context
export const useAnimations = () => {
  const context = useContext(AnimationContext);

  if (!context) {
    throw new Error('useAnimations must be used within an AnimationProvider');
  }

  return context;
};

export default AnimationContext;
